import { UiFileItem } from '../../molecules/file-upload/file-upload.types';

export type UiInvoiceValidationStatus = 'XML válido' | 'XML inválido';

export type UiInvoiceSatField = { label: string; value: string };

export type UiInvoiceSatValidation = {
  generales: UiInvoiceSatField[];
  impuestos: UiInvoiceSatField[];
};

export type UiInvoiceDraftRow = {
  id: string;
  archivo: string;
  pdf: string;
  folio: string;
  fecha: string;
  subtotal: string;
  descuento: string;
  emisor: string;
  metodoPago: string;
  formaPago: string;
  usoCfdi: string;
  iva: string;
  retencionIsr: string;
  retencionIva: string;
  importe: string;
  /** Texto de la columna Validación en la captura manual. */
  estatus: 'Validado' | 'Error validación';
  /** El XML y el PDF comparten el mismo nombre, sin extensión. */
  namesMatch: boolean;
  validacion: UiInvoiceValidationStatus;
  /** Null cuando el PDF no tiene un XML con el mismo nombre. */
  xml: UiFileItem | null;
  /** PDF emparejado con el XML. Null si no hubo coincidencia. */
  pdfFile: UiFileItem | null;
  sat: UiInvoiceSatValidation | null;
};

export type UiInvoiceCaptureItem = {
  folio: string;
  fecha: string;
  subtotal: string;
  descuento: string;
  emisor: string;
  rfcEmisor: string;
  metodoPago: string;
  formaPago: string;
  usoCfdi: string;
  iva: string;
  retencionIsr: string;
  retencionIva: string;
  importe: string;
  validacion: UiInvoiceValidationStatus;
  pdf: UiFileItem[];
  xml: UiFileItem[];
  sat: UiInvoiceSatValidation | null;
};

export type UiInvoiceCaptureResult = {
  notes: string;
  items: UiInvoiceCaptureItem[];
};

export function invoiceFileBaseName(item: UiFileItem): string {
  return item.name.replace(/\.[^.]+$/, '').toLocaleLowerCase();
}

/** Arma el resultado de guardado: PDF emparejado por nombre base con cada XML. */
export function takeInvoiceCaptureResult(
  files: readonly UiFileItem[],
  drafts: readonly UiInvoiceDraftRow[],
  notes: string,
): UiInvoiceCaptureResult | null {
  const pdfFiles = files.filter(item => item.extension === 'pdf');
  if (pdfFiles.length === 0 || drafts.length === 0) return null;

  const items = drafts.map(draft => {
    const xml = draft.xml;
    const matchingPdf = xml
      ? pdfFiles.filter(pdf => invoiceFileBaseName(pdf) === invoiceFileBaseName(xml))
      : [];
    const pdf = draft.pdfFile
      ? [draft.pdfFile]
      : matchingPdf.length > 0
        ? matchingPdf
        : drafts.length === 1
          ? [...pdfFiles]
          : [];
    return {
      folio: draft.folio,
      fecha: draft.fecha,
      subtotal: draft.subtotal,
      descuento: draft.descuento,
      emisor: draft.emisor,
      rfcEmisor: draft.sat?.generales.find(field => field.label === 'RFC')?.value ?? '—',
      metodoPago: draft.metodoPago,
      formaPago: draft.formaPago,
      usoCfdi: draft.usoCfdi,
      iva: draft.iva,
      retencionIsr: draft.retencionIsr,
      retencionIva: draft.retencionIva,
      importe: draft.importe,
      validacion: draft.validacion,
      pdf,
      xml: draft.xml ? [draft.xml] : [],
      sat: draft.sat,
    };
  });

  return { notes, items };
}
